import dayjs from 'dayjs'
import type { MappingRule } from '../types'
import { transformRows } from './transformer'

const WINDOWS_RESERVED_FILENAME_CHARS = new Set('<>:"/\\|?*')

function sanitizeClientPublicKeyForFilename(s: string): string {
  let out = ''
  for (const ch of s) {
    const code = ch.charCodeAt(0)
    if (code < 32 || WINDOWS_RESERVED_FILENAME_CHARS.has(ch)) {
      out += '_'
    } else {
      out += ch
    }
  }
  return out
}

/** `"{CPK}_MM_DD_YYYY_HH_mm_ss.json"` for mapping rules export */
export function buildMappingRulesExportFilename(
  clientPublicKey: string
): string {
  const safe =
    clientPublicKey.trim() !== ''
      ? sanitizeClientPublicKeyForFilename(clientPublicKey)
      : 'unknown'
  return `${safe}_${dayjs().format('MM_DD_YYYY_HH_mm_ss')}.json`
}

export function exportTransformedCsv(
  mappingRules: MappingRule[],
  rows: Record<string, string>[],
  computedFields: MappingRule[] = []
): void {
  const transformed = transformRows(mappingRules, rows, computedFields)
  const headers = mappingRules.map((r) => r.target).filter(Boolean)

  // transformRows adds an `Errors` column for records blocked by a rule (e.g.
  // a CONCAT source that was empty). Append it so failed joins are visible in
  // the export instead of masquerading as successful empty values.
  const hasErrors = transformed.some((row) => row.Errors)
  if (hasErrors && !headers.includes('Errors')) headers.push('Errors')

  const csvLines = [
    headers.join(','),
    ...transformed.map((row) =>
      headers.map((h) => `"${(row[h] ?? '').replace(/"/g, '""')}"`).join(',')
    ),
  ]

  const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'transformed-output.csv'
  a.click()
  URL.revokeObjectURL(url)
}

export function downloadJsonBlob(json: string, filename: string): void {
  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
